import ValidationHelper from "@/utils/validation.util";
import PhoneInput, { parsePhoneNumber } from 'react-phone-number-input'
import { useEffect, useMemo, useRef, useState} from "react";
import parsePhoneNumberFromString, { CountryCode, E164Number, getCountryCallingCode } from "libphonenumber-js/min";
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { getCountries } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import en from 'react-phone-number-input/locale/en.json';
import de from 'react-phone-number-input/locale/de.json';
import style from './style.module.scss';
import { getMandatorDataAll, getSessionUserData } from "@/services/api";
import { QueryKeys } from "@/enums/query-keys.enum";
import { useQuery } from "@tanstack/react-query";
import useTranslation from "@/hooks/useTranslate.hook";
import { LocalStorageItems } from "@/enums/local-storage.enum";

type InputPhoneNumberType = {
    errorMsg?: string;
    label?: string;
    name: string;
    id?: string;
    placeholder?: string;
    required?: boolean;
    onChange: Function;
    value?: string | null;
    defaultCountry?: any;
    disabled?: boolean;
    readOnly?: boolean;
    customClass?: any;
    labelTooltip?: any;
}

const InputPhoneNumber = ({ 
        errorMsg, 
        label, 
        name, 
        placeholder, 
        required = false, 
        onChange, 
        value, 
        defaultCountry, 
        disabled, 
        customClass, 
        readOnly = false, 
        labelTooltip = undefined}: InputPhoneNumberType) => {
    const { t } = useTranslation(["responses"]);
    const [ touched, setTouched ] = useState(false);
    const [ localValue, setLocalValue ] = useState<E164Number>();

    const [ countries ] = useState(getCountries().map(code => ({code, name: localStorage.getItem(LocalStorageItems.SelectedLanguage) === "de" ? de[code] : en[code]})));

    const countryLabels = countries.reduce<Record<string, string>>((acc: any, country: any) => {
            acc[country.code] = country.name;
            return acc;
        }, {}
    );
    countryLabels.country = t("country");
    countryLabels.ZZ = t("international");

    const { data: sessionUserData, isFetched: isFetchedSessionData } = useQuery({
        queryKey: [QueryKeys.SessionUserData],
        queryFn: () => getSessionUserData(),
        enabled: !value && !defaultCountry, gcTime: 1200000, staleTime: 600000
    });

    const { data: mandatorData, isLoading } = useQuery({
        queryKey: [QueryKeys.MandatorDataAll],
        queryFn: () => getMandatorDataAll(sessionUserData.mandatorId),
        enabled: !value && !defaultCountry, gcTime: 1200000, staleTime: 600000 
    });

    const handleChange = (value: E164Number | undefined) => {
        setLocalValue(value);
        onChange(value?.toString());

        if (!touched) setTouched(true);
    };

    const finalDefaultCountry = defaultCountry ? defaultCountry : ((!value && !isLoading) ? countries.find(country => country.name === t(`backend_api.countries.${mandatorData.country}`))?.code : undefined)

    const isValid = useMemo(() => {
        if (!touched) return true; // Before interaction, assume valid
        
        if (required && ValidationHelper.isPropertyNullOrEmpty(value?.toString())) {
            return false;
        }
    
        if (!!value && !isPossiblePhoneNumber(value?.toString())) {
            return false;
        }
    
        return true;
    }, [touched, value, required]);

    useEffect(() => {
        if (value && !localValue) {
            setLocalValue(value as E164Number);
        }
    }, [value])

    const skipNextOnChange = useRef(false);

    return (
        <div className={
                `c-input ${customClass} 
                ${(!isValid && touched) && 'c-input--error'}
                ${disabled && 'c-input--disabled'}
                ${readOnly && 'c-input--read-only'}`
            }
        >
            {!ValidationHelper.isPropertyNullOrEmpty(label) && (
                <label htmlFor={name}>
                    {label}
                    {labelTooltip}
                </label>
            )}
            <div className="c-input__wrap">
                <PhoneInput
                    international
                    className={style.cPhoneInput}
                    disabled={disabled}
                    defaultCountry={finalDefaultCountry}
                    placeholder={placeholder}
                    value={localValue}
                    onChange={(value) => {
                        if (skipNextOnChange.current) {
                            skipNextOnChange.current = false;
                            return;
                        } 
                        handleChange(value)
                    }}
                    onCountryChange={(country) => {
                        if (!!country && !!value) {
                            let phoneNumber = parsePhoneNumberFromString(value as E164Number);
                            if (phoneNumber) {
                                let newCountryPrefix = getCountryCallingCode(country);
                                skipNextOnChange.current = true;
                                handleChange(("+" + newCountryPrefix + phoneNumber.nationalNumber) as E164Number);
                            }
                        }
                    }}
                    labels={countryLabels}/>
            </div>
            {errorMsg && !isValid && touched && (
                <span className="c-input__error">{errorMsg}</span>
            )}
        </div>
    );
};

export default InputPhoneNumber;
